/**
 * A letter on stationery (toiyp Plus): the words laid out on sheets of the
 * chosen paper, inside its writing area only, so they never sit on the art.
 * A long letter continues onto more sheets of the same design.
 *
 * Sheets keep the paper's own proportions and fill the width they are given
 * (up to a comfortable reading width). The text keeps the letter's usual size
 * and line height; pagination is measured in the DOM against a hidden copy of
 * the writing area, and redone when the width changes.
 *
 * A reader can always switch to plain paper (`usePlainPaper`) — remembered on
 * this device — for legibility; the letter then reads exactly as before.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Stationery } from '../letters/api';
import { paginate } from './paginate';

const MAX_SHEET_WIDTH = 560;
/** The stamp sits on the first sheet when the art above the writing area is this tall (px). */
const STAMP_ROOM = 96;

const PLAIN_KEY = 'toiyp:plain-paper';

function readPlain(): boolean {
  try {
    return localStorage.getItem(PLAIN_KEY) === '1';
  } catch {
    return false;
  }
}

const listeners = new Set<(v: boolean) => void>();

/** The reader's "plain paper" choice, shared by every letter on screen. */
export function usePlainPaper(): [boolean, (v: boolean) => void] {
  const [plain, setPlainState] = useState(readPlain);
  useEffect(() => {
    listeners.add(setPlainState);
    return () => {
      listeners.delete(setPlainState);
    };
  }, []);
  const setPlain = useCallback((v: boolean) => {
    try {
      localStorage.setItem(PLAIN_KEY, v ? '1' : '0');
    } catch {
      /* a private window: this visit only */
    }
    listeners.forEach((l) => l(v));
  }, []);
  return [plain, setPlain];
}

/** "On their paper · Plain paper" — the reader's switch, shown above a letter on stationery. */
export function PaperToggle({ paper }: { paper: Stationery }) {
  const [plain, setPlain] = usePlainPaper();
  return (
    <div className="paper-toggle" role="group" aria-label="How to show this letter">
      <button type="button" className={`paper-toggle__opt${plain ? '' : ' is-on'}`} aria-pressed={!plain} onClick={() => setPlain(false)}>
        On {paper.name}
      </button>
      <button type="button" className={`paper-toggle__opt${plain ? ' is-on' : ''}`} aria-pressed={plain} onClick={() => setPlain(true)}>
        Plain paper
      </button>
    </div>
  );
}

export function LetterPaper({
  paper,
  text,
  stamp,
  className = '',
}: {
  paper: Stationery;
  text: string;
  /** Placed in the first sheet's top-right corner when the art leaves room; above it otherwise. */
  stamp?: ReactNode;
  className?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const measure = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [pages, setPages] = useState<string[]>([text]);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const update = () => setWidth(Math.min(MAX_SHEET_WIDTH, Math.floor(el.clientWidth)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also on window resize: a hidden or backgrounded view may not report through the observer.
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const box = useMemo(() => {
    const h = (width * paper.height) / paper.width;
    const a = paper.area;
    return {
      sheetHeight: h,
      left: a.left * width,
      top: a.top * h,
      width: (1 - a.left - a.right) * width,
      height: (1 - a.top - a.bottom) * h,
    };
  }, [width, paper]);

  useLayoutEffect(() => {
    const m = measure.current;
    if (!m || box.width <= 0) return;
    const fits = (chunk: string) => {
      m.textContent = chunk;
      return m.scrollHeight <= m.clientHeight + 1;
    };
    setPages(paginate(text, fits));
  }, [text, box.width, box.height]);

  const stampOnSheet = stamp != null && box.top >= STAMP_ROOM;
  const sheet = {
    width,
    height: box.sheetHeight,
    backgroundImage: `url(${paper.imageUrl})`,
  } as React.CSSProperties;
  const textBox = {
    left: box.left,
    top: box.top,
    width: box.width,
    height: box.height,
    color: paper.ink,
  } as React.CSSProperties;

  return (
    <div className={`letter-paper ${className}`} ref={wrap}>
      {stamp != null && !stampOnSheet && <div className="letter-paper__stamp-above">{stamp}</div>}
      {/* The hidden copy of one writing area, for measuring what fits. */}
      <div className="paper-sheet__text paper-sheet__measure" ref={measure} style={textBox} aria-hidden="true" />
      {width > 0 &&
        pages.map((page, i) => (
          <div key={i} className="paper-sheet" style={sheet} aria-label={pages.length > 1 ? `Sheet ${i + 1} of ${pages.length}` : undefined}>
            {i === 0 && stampOnSheet && <div className="paper-sheet__stamp">{stamp}</div>}
            <div className="paper-sheet__text" style={textBox}>
              {page}
            </div>
            {pages.length > 1 && (
              <span className="paper-sheet__page" aria-hidden="true">
                {i + 1}/{pages.length}
              </span>
            )}
          </div>
        ))}
    </div>
  );
}
